/**
 * FraudRuleBuilderPage — `/settings/fraud-rules`.
 *
 * Visual rule editor for fraud-detection conditions. Backed by the generic
 * settings-collection store on service-audit
 * (`GET / PUT /v1/settings-collections/fraud_rules`).
 */
import { type JSX, useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Save, ShieldAlert, Trash2 } from 'lucide-react';

import {
  getSettingsCollection,
  putSettingsCollection,
} from '../../lib/api/settings';
import { PageHeader } from '../shared/StaffUI';

interface Condition {
  readonly id: string;
  readonly field: string;
  readonly operator: '>' | '<' | '=' | '!=' | 'in' | 'not_in';
  readonly value: string;
}

interface Rule {
  readonly id: string;
  readonly name: string;
  readonly severity: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
  readonly action: 'FLAG' | 'BLOCK' | 'REVIEW';
  readonly conditions: readonly Condition[];
}

const COLLECTION_KEY = 'fraud_rules' as const;

const FIELDS = [
  'transaction.amountMinor',
  'transaction.channel',
  'customer.kycLevel',
  'customer.txnsLastHour',
  'destination.country',
];

export function FraudRuleBuilderPage(): JSX.Element {
  const qc = useQueryClient();
  const query = useQuery({
    queryKey: ['settings-collection', COLLECTION_KEY],
    queryFn: () => getSettingsCollection<Rule>(COLLECTION_KEY),
    retry: false,
    staleTime: 30_000,
  });

  const [rules, setRules] = useState<readonly Rule[]>([]);
  const [dirty, setDirty] = useState(false);
  const [name, setName] = useState('');
  const [severity, setSeverity] = useState<Rule['severity']>('MEDIUM');
  const [action, setAction] = useState<Rule['action']>('FLAG');
  const [draftConditions, setDraftConditions] = useState<readonly Condition[]>([]);
  const [field, setField] = useState(FIELDS[0] ?? 'amount');
  const [op, setOp] = useState<Condition['operator']>('>');
  const [value, setValue] = useState('');

  useEffect(() => {
    if (query.data && !dirty) {
      setRules(query.data.items);
    }
  }, [query.data, dirty]);

  const saveMut = useMutation({
    mutationFn: (next: readonly Rule[]) =>
      putSettingsCollection<Rule>(COLLECTION_KEY, next),
    onSuccess: async () => {
      setDirty(false);
      await qc.invalidateQueries({ queryKey: ['settings-collection', COLLECTION_KEY] });
    },
  });

  function addCondition(): void {
    if (!value.trim()) return;
    setDraftConditions([
      ...draftConditions,
      { id: `c-${Date.now()}`, field, operator: op, value: value.trim() },
    ]);
    setValue('');
  }

  function saveRule(): void {
    if (!name.trim() || draftConditions.length === 0) return;
    setRules([
      ...rules,
      { id: `r-${Date.now()}`, name: name.trim(), severity, action, conditions: draftConditions },
    ]);
    setDirty(true);
    setName('');
    setDraftConditions([]);
  }

  function removeRule(id: string): void {
    setRules(rules.filter((r) => r.id !== id));
    setDirty(true);
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Fraud rule builder"
        description="Define conditions, severity and automated action for fraud detection rules."
        actions={
          dirty ? (
            <button
              type="button"
              onClick={() => saveMut.mutate(rules)}
              disabled={saveMut.isPending}
              className="inline-flex items-center gap-1.5 px-3 py-2 bg-nipost-green text-white rounded-lg text-sm font-medium disabled:opacity-60"
            >
              <Save className="w-4 h-4" /> {saveMut.isPending ? 'Saving…' : 'Save changes'}
            </button>
          ) : null
        }
      />

      {query.isPending ? (
        <p className="text-sm text-muted">Loading fraud rules…</p>
      ) : query.isError ? (
        <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-xs text-red-900">
          Could not load fraud rules: {(query.error as Error).message}
        </div>
      ) : null}

      {saveMut.isError ? (
        <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-xs text-red-900">
          Save failed: {(saveMut.error as Error).message}
        </div>
      ) : null}

      <div className="bg-white border border-border rounded-xl p-5">
        <div className="flex items-center gap-2 mb-3">
          <ShieldAlert className="w-4 h-4 text-nipost-green" />
          <h3 className="font-semibold text-gray-900">New rule</h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Rule name"
            aria-label="Rule name"
            className="px-3 py-2 text-sm border border-border rounded-lg"
          />
          <select
            value={severity}
            onChange={(e) => setSeverity(e.target.value as Rule['severity'])}
            aria-label="Severity"
            className="px-3 py-2 text-sm border border-border rounded-lg"
          >
            <option>LOW</option>
            <option>MEDIUM</option>
            <option>HIGH</option>
            <option>CRITICAL</option>
          </select>
          <select
            value={action}
            onChange={(e) => setAction(e.target.value as Rule['action'])}
            aria-label="Action"
            className="px-3 py-2 text-sm border border-border rounded-lg"
          >
            <option>FLAG</option>
            <option>BLOCK</option>
            <option>REVIEW</option>
          </select>
        </div>

        <div className="border border-dashed border-border rounded-lg p-3 mb-3">
          <p className="text-xs font-semibold uppercase text-muted mb-2">Conditions</p>
          {draftConditions.length === 0 ? (
            <p className="text-xs text-muted">No conditions yet — add one below.</p>
          ) : (
            <ul className="space-y-1 mb-2">
              {draftConditions.map((c) => (
                <li key={c.id} className="text-xs font-mono">
                  {c.field} {c.operator} {c.value}
                </li>
              ))}
            </ul>
          )}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
            <select
              value={field}
              onChange={(e) => setField(e.target.value)}
              aria-label="Field"
              className="px-2 py-1.5 text-xs border border-border rounded"
            >
              {FIELDS.map((f) => (
                <option key={f}>{f}</option>
              ))}
            </select>
            <select
              value={op}
              onChange={(e) => setOp(e.target.value as Condition['operator'])}
              aria-label="Operator"
              className="px-2 py-1.5 text-xs border border-border rounded"
            >
              <option value=">">{'>'}</option>
              <option value="<">{'<'}</option>
              <option value="=">{'='}</option>
              <option value="!=">{'!='}</option>
              <option value="in">in</option>
              <option value="not_in">not_in</option>
            </select>
            <input
              type="text"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder="Value"
              aria-label="Value"
              className="px-2 py-1.5 text-xs border border-border rounded"
            />
            <button
              type="button"
              onClick={addCondition}
              className="px-3 py-1.5 text-xs font-medium text-white bg-nipost-green rounded"
            >
              Add condition
            </button>
          </div>
        </div>

        <button
          type="button"
          onClick={saveRule}
          className="inline-flex items-center gap-1 px-3 py-2 text-xs font-medium text-white bg-nipost-green rounded-lg"
        >
          <Plus className="w-3.5 h-3.5" /> Save rule
        </button>
      </div>

      <div className="bg-white border border-border rounded-xl p-5 space-y-3">
        <h3 className="font-semibold text-gray-900">Rules ({rules.length})</h3>
        {rules.length === 0 ? (
          <p className="text-sm text-muted">No rules configured.</p>
        ) : (
          <ul className="divide-y divide-border">
            {rules.map((r) => (
              <li key={r.id} className="py-3 flex items-start justify-between gap-3">
                <div className="flex-1">
                  <p className="text-sm font-semibold text-gray-900">{r.name}</p>
                  <p className="text-xs text-muted">
                    {r.severity} · {r.action} · {r.conditions.length} conditions
                  </p>
                  <ul className="mt-1 text-[11px] font-mono text-muted">
                    {r.conditions.map((c) => (
                      <li key={c.id}>
                        {c.field} {c.operator} {c.value}
                      </li>
                    ))}
                  </ul>
                </div>
                <button
                  type="button"
                  onClick={() => removeRule(r.id)}
                  aria-label={`Remove ${r.name}`}
                  className="text-red-600 hover:bg-red-50 rounded p-1"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
